import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule } from "@angular/forms";
import { catchError, finalize } from "rxjs";
import { ToastrService } from "ngx-toastr";

// NG-ZORRO Imports
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzGridModule } from 'ng-zorro-antd/grid';

import { Breadcrumb, GoogleFileDto, PermissionEnum } from "../../../dto";
import { HttpFilesService } from "../../../services/files/http-files.service";
import { NzDividerComponent, NzDividerModule } from "ng-zorro-antd/divider";
import { AuthService } from "../../../services/auth/auth.service";
import { UnauthorizedAccessEditWarningComponent } from "../../../components/unauthorized-access-edit-warning/unauthorized-access-edit-warning.component";



@Component({
    selector: 'app-view-files',
    standalone: true,
    imports: [
        CommonModule, 
        ReactiveFormsModule, 
        NzTableModule,
        NzButtonModule,
        NzIconModule,
        NzCardModule,
        NzListModule,
        NzBreadCrumbModule,
        NzGridModule,
        NzDividerModule,
        UnauthorizedAccessEditWarningComponent
    ],
    templateUrl: './view-files.component.html',
    styleUrls: ['./view-files.component.scss']
})
export class ViewFilesComponent implements OnInit {

    files: GoogleFileDto[] = [];
    selectedFiles: GoogleFileDto[] = []; // The "Cart"
    loading = false; 
    
    currentFolderId?: string;;
    
    breadcrumbs: Breadcrumb[] = [{ id: this.currentFolderId, name: 'Home' }];

    constructor(
        private toastr: ToastrService, 
        private httpService: HttpFilesService,
        private authService: AuthService
    ) { }

    ngOnInit(): void {
        this.fetchFilesForFolder();
    }

    get canPackage() {
        return this.authService.hasAccess(PermissionEnum.FilesPackage);
    }

    fetchFilesForFolder() {
        this.loading = true;
        this.httpService.getAllFiles(this.currentFolderId) 
            .pipe(
                catchError(error => {
                    console.error('Error fetching files', error);
                    this.toastr.error('Failed to fetch files');
                    throw error;
                }),
                finalize(() => this.loading = false)
            )
            .subscribe(data => {
                this.files = data;
            });
    }

    openFolder(folder: GoogleFileDto) {
        if (folder.mimeType === 'application/vnd.google-apps.folder') {
            this.currentFolderId = folder.id;
            this.breadcrumbs.push({ id: folder.id, name: folder.name });
            this.fetchFilesForFolder();
        }
    }

    navigateToBreadcrumb(index: number, breadcrumb: Breadcrumb) {
        this.breadcrumbs = this.breadcrumbs.slice(0, index + 1);
        this.currentFolderId = breadcrumb.id;
        this.fetchFilesForFolder();
    }

    navigateToFolder(folderId: string) {
        this.toastr.info("Navigation logic to jump to: " + folderId);
    }

    isFolder(mimeType: string): boolean {
        return mimeType === 'application/vnd.google-apps.folder';
    }

    isFileSelected(fileId: string): boolean {
        return this.selectedFiles.some(f => f.id === fileId);
    }

    toggleSelection(file: GoogleFileDto) {
        if (this.isFileSelected(file.id)) {
            this.selectedFiles = this.selectedFiles.filter(f => f.id !== file.id);
        } else {
            this.selectedFiles.push(file);
        }
    }

    removeSelectedFile(fileId: string) {
        this.selectedFiles = this.selectedFiles.filter(f => f.id !== fileId);
    }

    packageAndDownload() {
        if (this.selectedFiles.length === 0) {
            this.toastr.warning('Please select files to package.');
            return;
        }

        this.loading = true;
        this.toastr.info(`Packaging ${this.selectedFiles.length} files. Please wait...`);

        this.httpService.packageAndDownload(this.selectedFiles)
            .pipe(
                catchError(error => {
                    console.error('Download error', error);
                    this.toastr.error('Failed to create SDK package');
                    throw error;
                }),
                finalize(() => this.loading = false)
            )
            .subscribe(response => {
                const blob = response.body;
                if (blob) {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'biorugged-sdk-package.zip'; 
                    document.body.appendChild(a);
                    a.click();
                    
                    window.URL.revokeObjectURL(url);
                    a.remove();
                    
                    this.toastr.success('Package downloaded successfully');
                    
                    this.selectedFiles = [];
                }
            });
    }
}